import { difference } from 'lodash-es';

import { arrToSnakeCase, ContactRolesEnum, getContactRoles } from '@/bundles/Contact/enums/contactRoles';
import { ITextValue } from '@/types/common';
import { IContactCardItem } from '@/bundles/Contact/interfaces';
import store from '@/store/store';

const getListByCategory = (category: string) => store.getters['Admin/getListByCategory'](category);

export const contactsCard = {
  availability: 'Availability',
  availabilityProperty: 'Property',
  pipeline_primary_client: 'Primary Client',
  requirement_contributor: 'Contributor',
};

export const contactCardsNoneLabelDictionary = {
  company: 'No contacts found for this company',
  pipeline: 'No contacts found for this pipeline',
  occupier: 'No contacts found for this occupier',
  contact: 'No contacts found for this contact',
  comparable: 'No contacts found for this comparable',
  property: 'No contacts found for this property',
  requirement: 'No contacts found for this requirement',
  pipeline_primary_client: 'No primary contacts found for this pipeline',
  requirement_contributor: 'No contributors found for this requirement',
};

export const contactPipelinePrimaryClientRoles = [
  ContactRolesEnum.local_manager,
  ContactRolesEnum.regional_manager,
  ContactRolesEnum.ultimate_decision_maker,
  ContactRolesEnum.key_stake_holder,
];

// SCBT-59: the contributor role is offered only on the requirement "Contributor" card;
// the regular requirement contacts card never lists it.
export const contactRequirementContributorRoles = [
  ContactRolesEnum.contributor,
];

export const getPipelineContactRoles = (roles: string[]): ITextValue<string>[] => {
  const snakeCaseRoles = arrToSnakeCase(roles) as ContactRolesEnum[];
  const nonPrimaryRoles = snakeCaseRoles.filter(role => !contactPipelinePrimaryClientRoles.includes(role));

  return getContactRoles(nonPrimaryRoles);
}

export const getContactRolesBySource = (source: string): ITextValue<string>[] => {
  const sourceType = source === 'availabilityProperty' ? 'property' : source;
  const categories = ['property', 'company', 'pipeline', 'occupier', 'availability', 'comparable', 'deal', 'requirement', 'pipeline_primary_client', 'requirement_contributor', 'comparable_sale', 'comparable_lease'];

  if (!categories.some(category => category === sourceType)) {
    return [];
  }

  if (sourceType === 'pipeline') {
    return getPipelineContactRoles(getListByCategory(`${sourceType}_roles`).items);
  }

  if (sourceType === 'pipeline_primary_client') {
    return getContactRoles(contactPipelinePrimaryClientRoles);
  }

  if (sourceType === 'requirement_contributor') {
    return getContactRoles(contactRequirementContributorRoles);
  }

  const roles = arrToSnakeCase(getListByCategory(`${sourceType}_roles`)?.items || []) as ContactRolesEnum[];

  if (sourceType === 'requirement') {
    return getContactRoles(roles.filter(role => !contactRequirementContributorRoles.includes(role)));
  }

  return getContactRoles(roles);
};

const isContributorContact = (contact: IContactCardItem): boolean => {
  const snakeCaseRoles = arrToSnakeCase(contact.roles || []) as ContactRolesEnum[];

  return contactRequirementContributorRoles.some(role => snakeCaseRoles.includes(role));
};

const hasNonContributorRole = (contact: IContactCardItem): boolean => {
  const snakeCaseRoles = arrToSnakeCase(contact.roles || []) as ContactRolesEnum[];

  return snakeCaseRoles.some(role => !contactRequirementContributorRoles.includes(role));
};

/**
 * Splits requirement contacts between the regular contacts card and the "Contributor" card.
 * - contributor card: contacts with the contributor role (the card fetches member-linked
 *   contacts only, see `contact_internal` in ContactsCard.loadContacts)
 * - regular card: everything that is not a contributor-only contact (contacts without any role
 *   still show under "No Role" there)
 */
export const mapRequirementContactsBySource = (contacts: IContactCardItem[], source: 'requirement' | 'requirement_contributor'): IContactCardItem[] => {
  if (!contacts?.length) {
    return [];
  }

  if (source === 'requirement_contributor') {
    return contacts.filter(isContributorContact);
  }

  return contacts.filter(contact => !isContributorContact(contact) || hasNonContributorRole(contact));
};

export const mapPipelineContactsBySource = (contacts: any[], source: 'pipeline_primary_client' | 'pipeline', pipelineContactRolesList): any[] => {
  if (!contacts.length) {
    return [];
  }

  const pipelineContactRoles = pipelineContactRolesList.map(item => item.value);
  const notPrimaryClientRoles = source === 'pipeline' ? difference(pipelineContactRoles, contactPipelinePrimaryClientRoles) : [];

  return contacts.filter(contact => {
    const snakeCaseRoles = arrToSnakeCase(contact.roles) as ContactRolesEnum[];
    const isPrimaryClient = contactPipelinePrimaryClientRoles.some(role => snakeCaseRoles.includes(role));
    const isRegularContact = notPrimaryClientRoles.some(role => snakeCaseRoles.includes(role));

    return source === 'pipeline_primary_client' ? isPrimaryClient : isRegularContact;
  });
}

export const sourceMappingDictionary = {
  availabilityProperty: 'property',
  pipeline_primary_client: 'pipeline',
  requirement_contributor: 'requirement',
};

export const isRequirementContactsSource = (source: string): source is 'requirement' | 'requirement_contributor' => {
  return source === 'requirement' || source === 'requirement_contributor';
};
